import { useEffect, useState } from "react";
import { apiFetch } from "@contractor-quoting/api-client";
import { RunbookWorkspace } from "./RunbookWorkspace.js";
import { SharedPricingWorkspace } from "./SharedPricingWorkspace.js";
import { PricingImportWorkspace } from "./PricingImportWorkspace.js";
import type { SharedPricingResponse } from "./quoting-setup-model.js";

type WorkspaceTab = "runbooks" | "pricing" | "import";

export function ConfigurationImportPage() {
  const [tab, setTab] = useState<WorkspaceTab>("runbooks");
  const [selectedRunbook, setSelectedRunbook] = useState<string | null>(null);
  const [hasPricing, setHasPricing] = useState(false);
  useEffect(() => {
    void apiFetch<SharedPricingResponse>("/v1/contractor/shared-pricing")
      .then((result) => setHasPricing(Boolean(result.current)))
      .catch(() => setHasPricing(false));
  }, [tab]);
  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
            Contractor configuration
          </p>
          <h1 className="mt-2 text-4xl font-black">Quoting setup</h1>
          <p className="mt-2 max-w-3xl text-stone-600">
            Start with Mezoniq runbooks, add your shared prices, and publish changes only when they
            are ready.
          </p>
        </div>
        <details className="relative">
          <summary className="cursor-pointer list-none rounded-xl border border-emerald-800 bg-white px-5 py-3 font-bold text-emerald-900">
            Download ▾
          </summary>
          <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-stone-200 bg-white p-2 shadow-xl">
            <DownloadLink href="/api/v1/contractor/configuration/pricing_book/template">
              Shared pricing template (.xlsx)
            </DownloadLink>
            {hasPricing ? (
              <DownloadLink href="/api/v1/contractor/shared-pricing/export">
                Current shared pricing (.xlsx)
              </DownloadLink>
            ) : null}
            {selectedRunbook ? (
              <DownloadLink href={`/api/v1/contractor/runbooks/${selectedRunbook}/export`}>
                Current runbook (.xlsx)
              </DownloadLink>
            ) : null}
          </div>
        </details>
      </header>
      <nav
        aria-label="Quoting setup sections"
        className="mt-7 flex gap-2 overflow-x-auto border-b border-stone-300"
      >
        {(["runbooks", "pricing", "import"] as WorkspaceTab[]).map((item) => (
          <button
            aria-current={tab === item ? "page" : undefined}
            className={`whitespace-nowrap border-b-4 px-5 py-3 font-black ${tab === item ? "border-emerald-800 text-emerald-900" : "border-transparent text-stone-500"}`}
            key={item}
            onClick={() => setTab(item)}
          >
            {item === "pricing"
              ? "Shared pricing"
              : item === "import"
                ? "Import & export"
                : "Runbooks"}
          </button>
        ))}
      </nav>
      <main className="mt-6">
        {tab === "runbooks" ? (
          <RunbookWorkspace selected={selectedRunbook} onSelected={setSelectedRunbook} />
        ) : tab === "pricing" ? (
          <SharedPricingWorkspace onOpenImport={() => setTab("import")} />
        ) : (
          <PricingImportWorkspace />
        )}
      </main>
    </div>
  );
}

function DownloadLink({ href, children }: React.PropsWithChildren<{ href: string }>) {
  return (
    <a
      className="block rounded-lg px-3 py-2 font-semibold text-stone-700 hover:bg-emerald-50 hover:text-emerald-900"
      href={href}
    >
      {children}
    </a>
  );
}
